import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { SmbBundle } from "components/licenses/bundles";
import Modal from "components/modal/Modal";
import NewModelPricingTierView from "components/tenants/NewModelPricingTierView";
import OldModelPricingTierView from "components/tenants/OldModelPricingTierView";
import style from "components/tenants/pricing-tier-view.scss";
import { CombinedTier, LicensingModel, TenantType } from "domain/tenants";
import { generateTierToTranslationMap } from "domain/tierRelatedMaps";
import buttons from "styles/buttons.scss";

import testIds from "testIds.json";

interface Props {
    tenantType: TenantType;
    visibility: boolean;
    setVisibility: (visibility: boolean) => void;
    selectedTier: CombinedTier;
    tenantModal: LicensingModel;
    currentProductsInRateCard?: string[];
}

export const CHECKMARK_SIZE = 18;
export const STROKE_WIDTH = 1.5;

export function updateSelectedTierTitle(selected: boolean, tierTitle: string, selectedTier: string, t: TFunction) {
    return selected
        ? t("AddCustomerForm.pricingTierDialog.header.currentPlan", {
              tierPlan: generateTierToTranslationMap(t).get(selectedTier),
          })
        : (generateTierToTranslationMap(t).get(tierTitle) ?? "");
}

export function updateSelectedSmbTierTitle(selected: boolean, tierTitle: string, selectedTier: string, t: TFunction) {
    const getCombinedTierKey = (tier: string) => {
        if (tier == SmbBundle.SELECT_50 || tier == SmbBundle.SELECT_100) {
            return "AddCustomerForm.pricingTierDialog.header.combineSelect50And100";
        }
        if (tier == SmbBundle.SELECT_PLUS_250 || tier == SmbBundle.SELECT_PLUS_500) {
            return "AddCustomerForm.pricingTierDialog.header.combineSelect250And500";
        }
        return null;
    };

    const combinedSelectedKey = getCombinedTierKey(selectedTier);
    const combinedTitleKey = getCombinedTierKey(tierTitle);

    return selected
        ? t("AddCustomerForm.pricingTierDialog.header.currentPlan", {
              tierPlan: combinedSelectedKey
                  ? t(combinedSelectedKey)
                  : generateTierToTranslationMap(t).get(selectedTier),
          })
        : combinedTitleKey
          ? t(combinedTitleKey)
          : (generateTierToTranslationMap(t).get(tierTitle) ?? "");
}

const PricingTierView = (props: Props): JSX.Element => {
    const { t } = useTranslation();

    return (
        <Modal
            isOpen={props.visibility}
            hideModal={() => {
                props.setVisibility(false);
            }}
            modalTitle={
                props.tenantModal === LicensingModel.OLD_MODEL
                    ? t("AddCustomerForm.pricingTierDialog.title")
                    : t("AddCustomerForm.pricingTierDialog.titleBundle")
            }
        >
            <div className={style.model}>
                {props.tenantModal == LicensingModel.OLD_MODEL ? (
                    <OldModelPricingTierView selectedTier={props.selectedTier} tenantType={props.tenantType} />
                ) : (
                    <NewModelPricingTierView
                        tenantType={props.tenantType}
                        tenantCreation={false}
                        selectedTier={props.selectedTier}
                        currentProductsInRateCard={props.currentProductsInRateCard}
                    />
                )}

                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        data-testid={testIds.common.dialog.closeButton}
                        onClick={() => props.setVisibility(false)}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default PricingTierView;
